// Client Types for the new comprehensive client system
// Generated: 2025-10-26

export interface Client {
  // Core Identifiers
  id: string
  name: string
  smartlead_client_id?: number
  status: 'Active' | 'Inactive' | 'Churned' | 'Deleted'
  
  // Core Information
  onboarding_notes?: string
  package_signed?: '3 Month POC' | 'Month to Month recurring'
  tam_target_size?: TamSizeOption
  attribution_window_days?: number
  domains_included?: string[]
  mailboxes_included?: string[]
  linkedin_accounts_count?: number
  cold_calling_included?: boolean
  reply_handling?: 'AI Auto-Reply' | 'Manual Review' | 'Hybrid' | 'None'
  // Retainer fields (migration 20260420150000; `phase` removed 2026-04-20 —
  // see rationale in ClientCoreInfoForm above)
  monthly_retainer_usd?: number | null
  retainer_currency?: 'USD' | 'GBP' | 'EUR' | 'CAD' | 'AUD' | null
  onboarding_date?: string | null
  attio_record_id?: string | null
  
  // Company Information
  company?: string
  website?: string
  main_contact_email?: string
  secondary_contact_email?: string
  billing_name?: string
  billing_email?: string
  
  // Team Information
  sdr_count?: number
  ae_count?: number
  meeting_links?: Record<string, string> // JSONB for multiple meeting links
  clickup_backlog_url?: string
  sales_team_emails?: string[]
  sales_team_names?: string[]
  sales_team_roles?: string[]
  sales_team_regions?: string[]
  sales_team_calendly_links?: string[]
  
  // Product/Services Information
  product_names?: string[]
  product_categories?: string // Free text for now
  product_description?: string
  icp_titles?: string[]
  icp_departments?: string[]
  geos?: string[]
  compatibility_requirements?: string
  suitability_requirements?: string
  
  // TAM & Blocklist
  tam_accounts_url?: string
  tam_contacts_url?: string
  tam_segments?: Record<string, any> // JSONB for filters/rules
  tam_uploaded_data?: Record<string, any> // JSONB for table data
  upload_type?: string
  discovery_calls_url?: string
  demo_calls_url?: string
  qualified_meetings_url?: string
  deals_won_url?: string
  deals_owned_url?: string
  request_demos_url?: string
  
  // CRM Sync Data
  crm_deals_won?: number
  crm_deals_with_opportunities?: number
  crm_demos_booked?: number
  crm_deals_requested_demo?: number
  crm_sign_ups?: number
  crm_last_sync?: string
  crm_sync_data?: Record<string, any>
  
  // Tech Stack
  crm_system?: string
  crm_credentials?: Record<string, any> // Encrypted
  sequencer_system?: string
  sequencer_credentials?: Record<string, any> // Encrypted
  enrichment_tools?: string[]
  enrichment_credentials?: Record<string, any> // Encrypted
  
  // Slack Channels
  // Retired 2026-09-02: main (leadership) and reporting can no longer be created.
  // The columns and any existing channels are kept -- daily-masterinbox-action-reminder
  // still reads slack_main_channel as a fallback.
  slack_main_channel?: string
  slack_reporting_channel?: string
  slack_sales_channel?: string
  slack_positive_replies_channel?: string
  slack_neutral_negative_replies_channel?: string
  // Created on demand from the client wizard, never with the default set.
  slack_meetings_booked_channel?: string
  slack_website_identification_channel?: string
  
  // Audit & Timestamps
  created_at?: string
  updated_at?: string
  created_by?: string
  updated_by?: string
}

export interface ClientContact {
  id: string
  client_id: string
  name: string
  email: string
  is_contract_signer?: boolean
  role?: string
  phone?: string
  created_at?: string
  updated_at?: string
}

export interface ClientCRMWebhookData {
  id: string
  client_id: string
  webhook_type: 'deals_won' | 'deals_with_opportunities' | 'demos_booked' | 'deals_requested_demo' | 'sign_ups'
  raw_data: Record<string, any>
  processed_data?: Record<string, any>
  processed_at?: string
  created_at?: string
}

// Form data interfaces for multi-step wizard
export interface ClientCoreInfoForm {
  name: string
  company?: string
  website: string
  onboarding_notes?: string
  package_signed?: '3 Month POC' | 'Month to Month recurring'
  tam_target_size?: TamSizeOption
  clickup_backlog_url?: string
  attribution_window_days?: number
  // Retainer fields (migration 20260420150000) — feed the dashboard ROI
  // hero "You Paid" line and will drive the Attio MCP sync once Jamie
  // populates `Current MRR` + Deal `Start Date` in Attio.
  //
  // NOTE: `phase` / `ClientPhase` / `CLIENT_PHASE_OPTIONS` /
  // `derivePhaseFromOnboardingDate` were removed on 2026-04-20. The
  // wireframe's "Phase" concept is POC-day-count based (Month 1/2/3),
  // not client-lifecycle based, and it already lives in
  // DashboardGoalsPanel. No consumer ever existed for a
  // ramp/active/mature/churn client-lifecycle badge, so we dropped it
  // rather than carrying dead schema.
  monthly_retainer_usd?: number | null
  retainer_currency?: RetainerCurrency | null
  onboarding_date?: string | null // ISO date (YYYY-MM-DD)
  attio_record_id?: string | null
}

export type RetainerCurrency = 'USD' | 'GBP' | 'EUR' | 'CAD' | 'AUD'
export const RETAINER_CURRENCY_OPTIONS: RetainerCurrency[] = ['USD', 'GBP', 'EUR', 'CAD', 'AUD']

export interface ClientTeamForm {
  main_contact_name?: string
  main_contact_email?: string
  main_contact_phone?: string
  secondary_contact_name?: string
  secondary_contact_email?: string
  secondary_contact_phone?: string
  billing_name?: string
  billing_email?: string
  sdr_count?: number
  ae_count?: number
  meeting_links?: Record<string, string>
  sales_team_emails?: string[]
  sales_team_names?: string[]
  sales_team_roles?: string[]
  sales_team_regions?: string[]
  sales_team_calendly_links?: string[]
  // Enhanced team member assignments
  team_members: TeamMember[]
  assignments: TeamMemberAssignment[]
}

export interface TeamMember {
  id: string
  name: string
  email: string
  role: 'SDR' | 'AE' | 'Manager' | 'Other'
  languages_spoken: string[]
  calendly_link?: string
  meeting_links?: Record<string, string>
  capacity?: {
    max_leads_per_month: number
    current_load: number
  }
}


// Enhanced Product and Territory Models
export interface Product {
  // Basic info
  id: string
  name: string
  category: string
  description: string
  
  // E-commerce platforms (inclusionary/exclusionary)
  ecom_platforms: {
    included: string[]
    excluded: string[]
  }
  
  // Tech stack requirements
  tech_stack: {
    required: string[]
    excluded: string[]
  }
  
  // Business KPIs
  business_metrics: {
    revenue_range?: { min: number, max: number }
    employee_count?: { min: number, max: number }
    order_volume?: { min: number, max: number }
    site_traffic?: { min: number, max: number }
    return_rate?: { min: number, max: number }
    custom_metrics?: Array<{ id: string, name: string, min?: number, max?: number, value?: string, logic?: 'AND' | 'OR' }>
  }
  
  // Geographic targeting
  geo_targeting: {
    regions: string[]
    countries_included: string[]
    countries_excluded: string[]
  }
  
  // Company size with definitions
  company_size: {
    smb: { definition: string, criteria: any }
    mid_market: { definition: string, criteria: any }
    enterprise: { definition: string, criteria: any }
  }
  
  // Legacy fields for backward compatibility
  languages: string[]
  geos: string[]
  company_sizes: string[]
  industries?: string[]
  icp_requirements?: ICPRequirements
}

export interface Territory {
  // Basic info
  id: string
  name: string
  
  // Geographic scope with countries
  geo_targeting: {
    regions: string[]
    countries_included: string[]
    countries_excluded: string[]
  }
  
  // Language requirements
  languages: string[]
  
  // Company size with clear definitions
  company_size_focus: {
    smb?: { kpi: string, min: number, max: number }
    mid_market?: { kpi: string, min: number, max: number }
    enterprise?: { kpi: string, min: number, max: number }
  }
  
  // Legacy fields for backward compatibility
  geo_regions: string[]
  industry_focus?: string[]
  icp_requirements?: ICPRequirements
}

export interface ICPRequirements {
  titles: string[]
  departments: string[]
  company_size?: string[]
  industries?: string[]
  technologies?: string[]
}

export interface TeamMemberAssignment {
  id: string
  team_member_id: string
  product_id: string
  territory_id: string
  languages?: string[]
  geos?: string[]
  company_size_focus?: string[]
  industry_focus?: string[]
  notes?: string
}

export interface TeamMember {
  id: string
  name: string
  email: string
  role: 'SDR' | 'AE' | 'Manager' | 'Other'
  languages_spoken: string[]
  calendly_link?: string
  meeting_links?: Record<string, string>
  capacity?: {
    max_leads_per_month: number
    current_load: number
  }
}

export interface ClientProductForm {
  products: Product[]
  territories: Territory[]
  // Legacy fields for backward compatibility
  product_names?: string[]
  product_categories?: string
  product_description?: string
  icp_titles?: string[]
  icp_departments?: string[]
  geos?: string[]
  compatibility_requirements?: string
  suitability_requirements?: string
}


export interface ICPRequirements {
  titles: string[]
  departments: string[]
  company_size?: string[]
  industries?: string[]
  technologies?: string[]
}


export interface ClientInfrastructureForm {
  domains_included?: string[]
  mailboxes_included?: Array<string | { firstName: string, lastName: string, title?: string, profilePictureUrl?: string }>
  linkedin_accounts_count?: number
  linkedin_account_names?: string[]
  linkedin_account_links?: string[]
  linkedin_heyreach_connected?: boolean[]
  cold_calling_included?: boolean
  reply_handling?: 'AI Auto-Reply' | 'Manual Review' | 'Hybrid' | 'None'
  pricing_data?: Record<string, { available: boolean; price?: number }> // Persisted pricing/availability data from Spaceship checks
}

export interface ClientTAMForm {
  tam_accounts_url?: string
  tam_contacts_url?: string
  tam_uploaded_files?: Array<{
    id: string
    name: string
    type: 'tam_accounts' | 'blocklist'
    size: number
    uploadDate: string
    recordCount: number
    columns: string[]
    status: 'uploaded' | 'processing' | 'imported' | 'error'
  }>
  tam_segments?: Record<string, any>
  tam_uploaded_data?: Record<string, any>
  upload_type?: string
  meetings_scheduled_url?: string // Formerly discovery_calls_url
  qualified_meetings_url?: string // Formerly demo_calls_url
  deals_won_url?: string // Formerly deals_won_url (renamed label)
  accounts_owned_url?: string // Formerly deals_owned_url
  demos_requested_url?: string // Formerly request_demos_url
  current_customers_url?: string // New field
  customer_signups_url?: string // New field
  churned_customers_url?: string // New field
  dnc_blocklist_url?: string // New field
}

export interface ClientCRMForm {
  crm_system?: string
  crm_credentials?: Record<string, any>
  sequencer_system?: string
  sequencer_credentials?: Record<string, any>
  enrichment_tools?: string[]
  enrichment_credentials?: Record<string, any>
}

export interface SlackChannelConfig {
  id: string
  name: string // e.g., "#client-uk-positive-replies"
  channel_id?: string // Slack channel ID after creation
  territory_id?: string // Optional: link to territory (for region-specific channels)
  territory_name?: string // e.g., "UK", "USA"
  team_member_ids: string[] // Array of team member IDs assigned to this channel
  status: 'not_set' | 'creating' | 'created' | 'error'
}

export interface ClientSlackForm {
  // Retired 2026-09-02: read-only carry-through so existing values survive a save.
  // The wizard no longer renders or creates these.
  slack_main_channel?: string // Main communication channel (retired)
  slack_main_team_member_ids?: string[] // All team member IDs for main channel (retired)
  slack_reporting_channel?: string // Leadership only (retired)
  slack_reporting_team_member_ids?: string[] // Reporting channel team member IDs (retired)
  
  // Multiple channels allowed (region-specific)
  slack_sales_channels?: SlackChannelConfig[] // Sales people per region
  slack_positive_reply_channels?: SlackChannelConfig[] // Sales + leadership per region
  slack_neutral_negative_reply_channels?: SlackChannelConfig[] // Sales + leadership per region
  
  // Legacy fields for backward compatibility
  slack_sales_channel?: string
  slack_positive_replies_channel?: string
  slack_neutral_negative_replies_channel?: string

  // On-demand channels: created one at a time from the wizard button, never
  // as part of the default set.
  slack_meetings_booked_channel?: string
  slack_meetings_booked_team_member_ids?: string[]
  slack_website_identification_channel?: string
  slack_website_identification_team_member_ids?: string[]
}

export interface ClientAssetsForm {
  case_studies_url?: string
  demo_recordings_url?: string
  sales_collateral_url?: string
  other_assets_url?: string
  assets_notes?: string
}

// Combined form data for the wizard
export interface ClientWizardForm {
  coreInfo: ClientCoreInfoForm
  team: ClientTeamForm
  product: ClientProductForm
  infrastructure: ClientInfrastructureForm
  crm: ClientCRMForm
  slack: ClientSlackForm
  assets: ClientAssetsForm
  tam: ClientTAMForm
}

// Dropdown options
export const PACKAGE_OPTIONS = [
  '3 Month POC',
  'Month to Month recurring'
] as const

export const TAM_SIZE_OPTIONS = [
  'Up to 1k leads',
  'Up to 2.5k leads',
  'Up to 5k leads',
  'Up to 10k leads',
  'Up to 15k leads',
  'Up to 20k leads',
  'Up to 30k leads',
  'Up to 40k leads',
  'Up to 50k leads',
  'Up to 75k leads',
  'Up to 100k leads',
  'Up to 150k leads',
  'Up to 200k leads',
  'Up to 250k leads',
  'Up to 350k leads',
  'Up to 500k leads',
  'Up to 1m leads',
  'Custom'
] as const

export type TamSizeOption = typeof TAM_SIZE_OPTIONS[number]

export const REPLY_HANDLING_OPTIONS = [
  'AI Auto-Reply',
  'Manual Review',
  'Hybrid',
  'None'
] as const

export const GEO_OPTIONS = [
  'North America',
  'Europe',
  'Asia-Pacific',
  'Australia',
  'South America',
  'Africa',
  'Global'
] as const

export const CRM_SYSTEM_OPTIONS = [
  'Salesforce',
  'HubSpot',
  'Pipedrive',
  'Zoho',
  'Custom',
  'None'
] as const

export const SEQUENCER_SYSTEM_OPTIONS = [
  'Smartlead',
  'Apollo',
  'Outreach',
  'SalesLoft',
  'Custom',
  'None'
] as const

export const ENRICHMENT_TOOL_OPTIONS = [
  'Clay',
  'Apollo',
  'ZoomInfo',
  'Clearbit',
  'Hunter',
  'Custom'
] as const

export const WEBHOOK_TYPE_OPTIONS = [
  'deals_won',
  'deals_with_opportunities',
  'demos_booked',
  'deals_requested_demo',
  'sign_ups'
] as const
